import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { issue, type ValidationIssue, type ValidationIssues } from './common'

/**
 * The weekly adherence summary (data-model.md, D-066).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS RECORDS SUMMARIES. IT NEVER RECORDS A CONSUMED QUANTITY.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Project Sola is not a food log. What the rules need is whether the plan was
 * followed closely enough to be judged — D-066 answers that with counts of days
 * and sessions, so counts of days and sessions are all this stores. There is no
 * field here for what was eaten, and adding one would need a new decision, not a
 * new interface.
 *
 * **D-096 narrowed the prohibition and did not lift it.** A `NutritionPlanRecord`
 * may now hold a calorie or protein *target*, because an authority the user
 * declared set it and they typed it in. What remains representable nowhere is an
 * *intake*: a food, a meal, a recipe, a micronutrient or any quantity describing
 * what was consumed. The guard in `weeklyEvidence.test.ts` was rewritten to
 * assert that narrower property rather than removed — see the note there for why
 * `unplannedMeals` is a count of occasions and not an exception to this.
 *
 * The counts feed D-007's hard filter through `AdherenceEvidence`: below the
 * judgeable tier the engine refuses to recommend eating less, because a plan
 * that was never followed was never tested. That mapping is a rule and arrives
 * at Checkpoint 2.3; this file only gathers the evidence it will read (D-060).
 */

/** Nutrition, step and protein adherence are all counted in days per week. */
const DAYS_IN_WEEK = 7

export interface AdherenceCheck {
  readonly nutritionDaysOnPlan: number
  readonly trainingSessionsPlanned: number
  readonly trainingSessionsCompleted: number
  readonly stepTargetDays?: number
  readonly proteinTargetDays?: number
  readonly unplannedMeals?: number
  /** Recorded so the rules can say what is missing, not to score the user. */
  readonly missedMeasurements?: boolean
  readonly missedPhotoSet?: boolean
  readonly notes?: string
}

export interface AdherenceCheckInput {
  readonly nutritionDaysOnPlan: number
  readonly trainingSessionsPlanned: number
  readonly trainingSessionsCompleted: number
  readonly stepTargetDays?: number
  readonly proteinTargetDays?: number
  readonly unplannedMeals?: number
  readonly missedMeasurements?: boolean
  readonly missedPhotoSet?: boolean
  readonly notes?: string
}

function validateDayCount(
  field: string,
  value: number | undefined,
  required: boolean,
): ValidationIssue | undefined {
  if (value === undefined) {
    return required ? issue(field, 'Give a number of days from 0 to 7.') : undefined
  }
  if (!Number.isInteger(value) || value < 0 || value > DAYS_IN_WEEK) {
    return issue(field, `That must be a whole number of days from 0 to ${DAYS_IN_WEEK}.`)
  }
  return undefined
}

export function validateAdherenceCheck(input: AdherenceCheckInput): ValidationIssues {
  const issues: ValidationIssue[] = []

  const dayFields: readonly [string, number | undefined, boolean][] = [
    ['nutritionDaysOnPlan', input.nutritionDaysOnPlan, true],
    ['stepTargetDays', input.stepTargetDays, false],
    ['proteinTargetDays', input.proteinTargetDays, false],
  ]
  for (const [field, value, required] of dayFields) {
    const problem = validateDayCount(field, value, required)
    if (problem !== undefined) issues.push(problem)
  }

  const { trainingSessionsPlanned: planned, trainingSessionsCompleted: completed } = input

  if (!Number.isInteger(planned) || planned < 0) {
    issues.push(issue('trainingSessionsPlanned', 'Planned sessions must be a whole number.'))
  }
  if (!Number.isInteger(completed) || completed < 0) {
    issues.push(issue('trainingSessionsCompleted', 'Completed sessions must be a whole number.'))
  }
  /* D-066 judges completion as a fraction of what was planned, so more completed
     than planned would produce an adherence figure above 100% and could push a
     week over the "high" tier that gates any adjustment. It is an entry error,
     not an achievement to record. */
  if (Number.isInteger(planned) && Number.isInteger(completed) && completed > planned) {
    issues.push(
      issue(
        'trainingSessionsCompleted',
        'Completed sessions cannot exceed the number planned. Raise the planned count instead.',
      ),
    )
  }

  if (input.unplannedMeals !== undefined) {
    if (!Number.isInteger(input.unplannedMeals) || input.unplannedMeals < 0) {
      issues.push(issue('unplannedMeals', 'That must be a whole number, or left blank.'))
    }
  }

  return issues
}

/** Build a validated adherence check. Pure — no clock, no storage. */
export function buildAdherenceCheck(
  input: AdherenceCheckInput,
): Result<AdherenceCheck, ValidationIssues> {
  const issues = validateAdherenceCheck(input)
  if (issues.length > 0) return err(issues)

  return ok({
    nutritionDaysOnPlan: input.nutritionDaysOnPlan,
    trainingSessionsPlanned: input.trainingSessionsPlanned,
    trainingSessionsCompleted: input.trainingSessionsCompleted,
    ...(input.stepTargetDays === undefined ? {} : { stepTargetDays: input.stepTargetDays }),
    ...(input.proteinTargetDays === undefined
      ? {}
      : { proteinTargetDays: input.proteinTargetDays }),
    ...(input.unplannedMeals === undefined ? {} : { unplannedMeals: input.unplannedMeals }),
    ...(input.missedMeasurements === undefined
      ? {}
      : { missedMeasurements: input.missedMeasurements }),
    ...(input.missedPhotoSet === undefined ? {} : { missedPhotoSet: input.missedPhotoSet }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  })
}
